import { useCallback, useEffect, useRef } from "react";

export interface UseBottomSheetDragOptions {
  onClose?: () => void;
  closeThreshold?: number;
  velocityThreshold?: number;
  enabled?: boolean;
  onDragStart?: () => void;
  onDragEnd?: (closing: boolean) => void;
  /** Set to false if the element unmounts on close (e.g., modals) to avoid flash */
  resetStylesOnClose?: boolean;
}

export interface UseBottomSheetDragResult {
  sheetRef: React.RefObject<HTMLDivElement | null>;
  handleProps: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    onMouseDown: (e: React.MouseEvent) => void;
  };
}

interface DragState {
  isDragging: boolean;
  startY: number;
  currentY: number;
  startTime: number;
  velocity: number;
}

const MIN_TIME_DELTA_MS = 10;

export function useBottomSheetDrag({
  onClose,
  closeThreshold = 0.3,
  velocityThreshold = 0.5,
  enabled = true,
  onDragStart,
  onDragEnd,
  resetStylesOnClose = true,
}: UseBottomSheetDragOptions = {}): UseBottomSheetDragResult {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<DragState>({
    isDragging: false,
    startY: 0,
    currentY: 0,
    startTime: 0,
    velocity: 0,
  });
  const cleanupRef = useRef<(() => void) | null>(null);
  const mouseCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      mouseCleanupRef.current?.();
    };
  }, []);

  const handleDragStart = useCallback(
    (clientY: number) => {
      if (!enabled) return;

      dragState.current = {
        isDragging: true,
        startY: clientY,
        currentY: clientY,
        startTime: Date.now(),
        velocity: 0,
      };

      if (sheetRef.current) {
        sheetRef.current.style.transition = "none";
      }

      onDragStart?.();
    },
    [enabled, onDragStart]
  );

  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!enabled || !dragState.current.isDragging || !sheetRef.current) return;

      const deltaY = clientY - dragState.current.startY;
      const timeDelta = Date.now() - dragState.current.startTime;

      if (timeDelta >= MIN_TIME_DELTA_MS) {
        dragState.current.velocity = (clientY - dragState.current.currentY) / timeDelta;
      }

      dragState.current.currentY = clientY;
      dragState.current.startTime = Date.now();

      const translateY = Math.max(0, deltaY);
      sheetRef.current.style.transform = `translateY(${translateY}px)`;
    },
    [enabled]
  );

  const handleDragEnd = useCallback(() => {
    if (!dragState.current.isDragging || !sheetRef.current) return;

    const deltaY = dragState.current.currentY - dragState.current.startY;
    const sheetHeight = sheetRef.current.offsetHeight;
    const dragRatio = deltaY / sheetHeight;
    const velocity = dragState.current.velocity;

    const shouldClose = dragRatio > closeThreshold || velocity > velocityThreshold;

    onDragEnd?.(shouldClose);

    cleanupRef.current?.();

    if (shouldClose) {
      sheetRef.current.style.transition = "transform 0.2s ease-out";
      sheetRef.current.style.transform = "translateY(100%)";

      const sheet = sheetRef.current;
      const handleTransitionEnd = () => {
        sheet.removeEventListener("transitionend", handleTransitionEnd);
        cleanupRef.current = null;
        if (resetStylesOnClose) {
          sheet.style.transition = "";
          sheet.style.transform = "";
        }
        onClose?.();
      };
      sheetRef.current.addEventListener("transitionend", handleTransitionEnd);
      cleanupRef.current = () => sheet.removeEventListener("transitionend", handleTransitionEnd);
    } else {
      sheetRef.current.style.transition = "transform 0.2s ease-out";
      sheetRef.current.style.transform = "";

      const sheet = sheetRef.current;
      const handleTransitionEnd = () => {
        sheet.removeEventListener("transitionend", handleTransitionEnd);
        cleanupRef.current = null;
        sheet.style.transition = "";
      };
      sheetRef.current.addEventListener("transitionend", handleTransitionEnd);
      cleanupRef.current = () => sheet.removeEventListener("transitionend", handleTransitionEnd);
    }

    dragState.current.isDragging = false;
  }, [closeThreshold, velocityThreshold, onClose, onDragEnd, resetStylesOnClose]);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleDragStart(e.touches[0].clientY);
    },
    [handleDragStart]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      handleDragMove(e.touches[0].clientY);
    },
    [handleDragMove]
  );

  const onTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientY);

      const onMouseMove = (moveEvent: MouseEvent) => {
        handleDragMove(moveEvent.clientY);
      };

      const cleanup = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        mouseCleanupRef.current = null;
      };

      const onMouseUp = () => {
        handleDragEnd();
        cleanup();
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      mouseCleanupRef.current = cleanup;
    },
    [handleDragStart, handleDragMove, handleDragEnd]
  );

  return {
    sheetRef,
    handleProps: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMouseDown,
    },
  };
}
